import type { IUser } from '@frontend/user/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolveCourierSender } from '../../utils/resolve-courier-sender';
import { useScreen } from '../useScreen';

interface ViewProps {
    resolving: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
    error: string | null;
    info: string | null;
    resetError: () => void;
}

const SENSOR_TIMEOUT = 10000;

const useOpenDropOffScanCourierTrackAndTrace = (): ViewProps => {
    const intl = useIntl();
    const { store, onBack, onSubmit: onContinue, updateWizardData } = useScreen();
    const { localUpdateTransaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });

    const [resolving, setResolving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastScannedCodeRef = useRef<string | undefined>(undefined);

    const clearSensorTimeout = () => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    };

    const findSubmitStep = () =>
        store?.steps.find((step) => step.screen === ScreenType.OPEN_DROP_OFF_SCAN_COURIER_TRACK_AND_TRACE && step.trigger === ScreenTrigger.SUBMIT);

    const storeShippingNotes = async (shippingNotes: string | undefined) => {
        const trimmed = shippingNotes?.trim();
        if (!trimmed) return;

        await localUpdateTransaction({ shipping_notes: trimmed });
        updateWizardData({ courierTrackingNumber: trimmed });
    };

    const advanceWithCourier = async (user: IUser, trackingNumber: string | undefined) => {
        if (!store) return;

        clearSensorTimeout();
        setResolving(true);
        setError(null);
        setInfo(null);

        try {
            await storeShippingNotes(trackingNumber);

            const sender = await resolveCourierSender(user, store.functionalitySettings, store.wizard.account_id, store.data.transaction?.id);
            if (sender) {
                updateWizardData({ senders: [sender] });
            }

            setResolving(false);
            onContinue(findSubmitStep());
        } catch (err) {
            console.error('Failed to process courier track-and-trace scan:', err);
            clearSensorTimeout();
            setResolving(false);
            setError(
                intl.formatMessage({
                    id: 'HOME.ERRORS.CODE_NOT_FOUND',
                    defaultMessage: 'No matching identification found. Please try again.'
                })
            );
        }
    };

    useScanners({
        id: 'OPEN_DROP_OFF_SCAN_COURIER_TRACK_AND_TRACE',
        onDeviceSensorReceived: (code) => {
            lastScannedCodeRef.current = code;
            setResolving(true);
            setError(null);
            setInfo(null);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                setResolving(false);
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });

    useIdentification({
        id: 'OPEN_DROP_OFF_SCAN_COURIER_TRACK_AND_TRACE',
        onUserCourierIdentified: (user, trackingNumber) => {
            void advanceWithCourier(user, trackingNumber);
        },
        onNoMatchingIdentificationFound: () => {
            clearSensorTimeout();
            setResolving(false);
            void (async () => {
                await storeShippingNotes(lastScannedCodeRef.current);
                setInfo(
                    intl.formatMessage({
                        id: 'kiosk.wizard.open-drop-off-scan-courier-track-and-trace.code-stored',
                        description: 'Hint shown when a scanned code is stored as shipping notes without a courier match',
                        defaultMessage: 'Code stored. You can continue without a matched courier.'
                    })
                );
            })();
        }
    });

    const onSubmit = () => {
        if (!store) return;
        clearSensorTimeout();
        setResolving(false);
        onContinue(findSubmitStep());
    };

    useEffect(() => {
        return () => {
            clearSensorTimeout();
        };
    }, []);

    return {
        resolving,
        onSubmit,
        onBack,
        error,
        info,
        resetError: () => setError(null)
    };
};

export default useOpenDropOffScanCourierTrackAndTrace;
